import clsx from 'clsx';
import React, { useState } from 'react';
import Button from '../../components/Button';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import ReportIcon from '../../assets/icons/report-icon.svg?react';
import ChevronDown from '../../assets/icons/chevron-down-icon.svg?react';
import { toggleSwitch } from '../../reducers/switch';
import { useDispatch } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';

interface OnboardOrganizationDetailsProps {
  otherClasses?: string;
}

const OnboardOrganizationDetails: React.FC<OnboardOrganizationDetailsProps> = ({
  otherClasses,
}) => {
  const onboardOrganizationDetailsClasses = clsx(
    otherClasses,
    'w-full flex-1 pt-14 px-15 md:px-25 lg:pr-14 pb-10 lg:pb-20 lg:pl-8 flex flex-col lg:justify-between gap-12 md:gap-100 lg:gap-20',
  );
  const dispatch = useDispatch();
  const [toggle, setToggle] = useState(true);
  const sizes = useButtonSizeLarge();

  return (
    <div className={onboardOrganizationDetailsClasses} data-testid="onboard-organization-details">
      <div className="flex w-full flex-col gap-24">
        <div className="flex w-full max-w-818 flex-col gap-15">
          <h1 className="h3 hidden lg:block">Onboard Organization</h1>
          <div className="flex items-center gap-3 lg:hidden">
            <button
              className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5"
              onClick={() => dispatch(toggleSwitch())}
            >
              <ToggleIcon />
            </button>
            <h3>Onboard Organization</h3>
          </div>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button text-lg leading-5 font-medium lg:text-xl lg:leading-6">
            Onboard new organizations to the EHS Navigator platform. Complete the form to create a new organization account with owner details.
          </p>
        </div>
        <div className="flex flex-col gap-25">
          <div className="flex gap-25">
            <Button size={sizes} href="/admin/onboard-organization/form">
              <ReportIcon className="[&>path]:fill-current" />
              Onboard Organization
            </Button>
          </div>
          <p className="font-inter-variable text-neutral-tones-text -tracking-button max-w-766 text-lg leading-5 font-medium italic lg:text-xl lg:leading-6">
            Ensure all required information is accurate before submitting. When onboarding without payment, the organization owner will receive an email with access instructions.
          </p>
        </div>
      </div>
      <div className="border-neutral-tones-text/35 rounded-10 w-full border bg-black/5 px-15 py-15">
        <button
          onClick={() => setToggle(!toggle)}
          className="font-inter-variable text-brand-byzantine -tracking-button flex w-full cursor-pointer items-center justify-between text-lg leading-6 font-bold"
        >
          <span className="flex-1 text-left">
            Onboarding Guidelines & Requirements
          </span>
          <ChevronDown
            className={clsx(
              '[&>path]:fill-neutral-tones-text global-transition h-8 w-8',
              toggle ? 'rotate-180' : '',
            )}
          />
        </button>
        <div
          className={clsx(
            'global-transition',
            toggle
              ? 'mt-15 overflow-visible'
              : 'mt-0 max-h-0 overflow-hidden',
          )}
        >
          <hr className="border-neutral-tones-text/10 border-[1px]" />
          <div className="mt-15 grid grid-cols-1 gap-25 lg:grid-cols-2">
            <div className="w-full">
              <h6 className="font-inter-variable text-neutral-tones-text -tracking-button mb-2.5 text-base leading-6 font-bold md:text-lg">
                Onboarding Workflow
              </h6>
              <p className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                When onboarding an organization without payment, follow this process:
              </p>
              <ol className="my-5 list-[decimal] pl-5 space-y-3">
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Submit Onboarding Form:</strong> Complete the organization and owner details form without checking the payment option.
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Owner Receives Email:</strong> The organization owner will receive an onboarding email with a call-to-action (CTA) button.
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Auto Access Request:</strong> When the owner clicks the CTA and visits the site, an automatic access request is generated.
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Platform Owner Review:</strong> The request appears in the Organization Requests section where you can accept or reject it with a reason.
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Owner Notification:</strong> The owner receives an email notification whether the request is approved or rejected.
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Re-request (if rejected):</strong> If rejected, the owner can submit a new access request.
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Approval Process:</strong> If approved, the owner will set their password and verify their email.
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Login Access:</strong> After password setup and email verification, the owner can log in to the platform.
                </li>
              </ol>
            </div>
            <div className="w-full">
              <h6 className="font-inter-variable text-neutral-tones-text -tracking-button mb-2.5 text-base leading-6 font-bold md:text-lg">
                Required Information
              </h6>
              <p className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                To successfully onboard an organization, you need to provide:
              </p>
              <ul className="my-5 list-[number] pl-5">
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Organization Details:</strong> Company name, industry type, size, address, and contact information.
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Owner Information:</strong> Full name, email, contact number, date of birth, and password.
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Subscription Details:</strong> License count and payment status (leave unchecked for manual approval workflow).
                </li>
              </ul>
              <p className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                <strong>Important:</strong> The owner email or organization name must be unique and not already registered in the system. License count determines the number of users that can be added to the organization.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardOrganizationDetails;

